import React, { useState, useEffect } from 'react';
import { getConnections, createConnection, deleteConnection, testConnection, discoverSchema, getSnapshot } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Database,
  Plus,
  Trash2,
  RefreshCw,
  Server,
  Eye,
  Table,
  ChevronDown,
  ChevronRight,
  Loader2,
  Search,
  CheckCircle2,
  AlertCircle,
  Clock
} from 'lucide-react';

const ConnectionsPage = () => {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [testing, setTesting] = useState({});
  const [discovering, setDiscovering] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [expandedDbs, setExpandedDbs] = useState({});
  const [expandedTables, setExpandedTables] = useState({});
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    host: '',
    port: 3306,
    username: '',
    password: ''
  });

  useEffect(() => { fetchConnections(); }, []);

  const fetchConnections = async () => {
    try {
      const data = await getConnections();
      setConnections(data);
    } catch (error) {
      toast.error('Failed to fetch connections');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const newConn = await createConnection(formData);
      setConnections(prev => [...prev, newConn]);
      setDialogOpen(false);
      setFormData({ name: '', host: '', port: 3306, username: '', password: '' });
      toast.success('Connection created! Auto-discovering databases...');
      // Poll for discovery completion
      setTimeout(() => fetchConnections(), 5000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create connection';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleTest = async (id) => {
    setTesting(prev => ({ ...prev, [id]: true }));
    try {
      const result = await testConnection(id);
      if (result.success) {
        toast.success(result.message || 'Connection successful!');
      } else {
        toast.error(result.message || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDiscover = async (id) => {
    setDiscovering(prev => ({ ...prev, [id]: true }));
    try {
      const result = await discoverSchema(id);
      toast.success(`Discovery complete: ${result.database_count} databases, ${result.total_tables} tables found`);
      fetchConnections();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Discovery failed';
      toast.error(msg);
    } finally {
      setDiscovering(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteConnection(id);
      setConnections(prev => prev.filter(c => c.id !== id));
      toast.success('Connection deleted');
    } catch (error) {
      toast.error('Failed to delete connection');
    }
  };

  const handleViewSchema = async (conn) => {
    setSchemaDialogOpen(true);
    setSchemaLoading(true);
    setSelectedSnapshot(null);
    try {
      const snapshot = await getSnapshot(conn.id);
      setSelectedSnapshot({ ...snapshot, connectionName: conn.name });
    } catch (error) {
      toast.error('No schema snapshot found. Run discovery first.');
      setSchemaDialogOpen(false);
    } finally {
      setSchemaLoading(false);
    }
  };

  const toggleDb = (name) => setExpandedDbs(prev => ({ ...prev, [name]: !prev[name] }));
  const toggleTable = (key) => setExpandedTables(prev => ({ ...prev, [key]: !prev[key] }));

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'in_progress': return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'failed': return <AlertCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="connections-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Database Connections
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Connect your MySQL servers — databases are auto-discovered
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-gold" data-testid="add-connection-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Server
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-gray-900">Add MySQL Server</DialogTitle>
              <DialogDescription className="text-gray-500">
                Enter your server credentials. Databases will be auto-discovered.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Connection Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Production Server"
                  className="bg-gray-50 border-gray-200 text-gray-900"
                  required
                  data-testid="conn-name-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2 space-y-2">
                  <Label className="text-gray-700">Host</Label>
                  <Input
                    value={formData.host}
                    onChange={(e) => setFormData(prev => ({ ...prev, host: e.target.value }))}
                    placeholder="db.example.com"
                    className="bg-gray-50 border-gray-200 text-gray-900"
                    required
                    data-testid="conn-host-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Port</Label>
                  <Input
                    type="number"
                    value={formData.port}
                    onChange={(e) => setFormData(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                    className="bg-gray-50 border-gray-200 text-gray-900"
                    required
                    data-testid="conn-port-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Username</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="db_user"
                  className="bg-gray-50 border-gray-200 text-gray-900"
                  required
                  data-testid="conn-username-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  className="bg-gray-50 border-gray-200 text-gray-900"
                  required
                  data-testid="conn-password-input"
                />
              </div>
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-xs text-blue-700">
                  <Search className="w-3.5 h-3.5 inline mr-1" />
                  All accessible databases will be automatically discovered after connecting.
                </p>
              </div>
              <DialogFooter className="mt-6">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-gray-500">Cancel</Button>
                <Button type="submit" disabled={creating} className="btn-gold" data-testid="save-connection-btn">
                  {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting...</> : 'Connect & Discover'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {connections.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {connections.map((conn) => (
            <Card key={conn.id} className="bg-white border-gray-200 hover:shadow-lg transition-all">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#D4AF37]/10">
                      <Server className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <CardTitle className="text-gray-900 text-lg">{conn.name}</CardTitle>
                      <CardDescription className="text-gray-500 text-xs flex items-center gap-1.5 mt-0.5">
                        {getStatusIcon(conn.discovery_status)}
                        {conn.discovery_status === 'completed'
                          ? `${conn.database_count || 0} databases discovered`
                          : conn.discovery_status === 'in_progress'
                            ? 'Discovering...'
                            : conn.discovery_status === 'failed'
                              ? 'Discovery failed'
                              : 'Pending discovery'}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-2 text-sm">
                    <Server className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">Host:</span>
                    <span className="text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {conn.host}:{conn.port}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm mt-2">
                    <span className="text-gray-500 ml-6">User:</span>
                    <span className="text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {conn.username}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleTest(conn.id)} disabled={testing[conn.id]}
                    className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50" data-testid={`test-conn-${conn.id}`}>
                    {testing[conn.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" />Test</>}
                  </Button>
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => handleDiscover(conn.id)} disabled={discovering[conn.id]}
                      className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50" data-testid={`discover-conn-${conn.id}`}>
                      {discovering[conn.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" />Discover</>}
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handleViewSchema(conn)}
                    className="flex-1 border-gray-200 text-gray-700 hover:bg-gray-50" data-testid={`view-schema-${conn.id}`}>
                    <Eye className="w-4 h-4 mr-1" />Schema
                  </Button>
                  {isAdmin && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-conn-${conn.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-white border-gray-200">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-gray-900">Delete Connection?</AlertDialogTitle>
                          <AlertDialogDescription className="text-gray-500">
                            This will permanently delete "{conn.name}" and all its schema data.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-gray-200 text-gray-700 hover:bg-gray-50">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(conn.id)} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-white border-gray-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Database className="w-12 h-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No connections yet</h3>
            <p className="text-gray-500 text-sm text-center max-w-sm mb-6">
              Add your MySQL server credentials and we'll automatically discover all databases, tables, indexes, and sample data.
            </p>
            <Button onClick={() => setDialogOpen(true)} className="btn-gold" data-testid="add-first-conn-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Server
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Schema Dialog - Multi-database view */}
      <Dialog open={schemaDialogOpen} onOpenChange={setSchemaDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Database className="w-5 h-5 text-[#D4AF37]" />
              Schema: {selectedSnapshot?.connectionName}
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              {selectedSnapshot?.database_count || 0} databases, {selectedSnapshot?.total_tables || 0} tables
              {selectedSnapshot?.discovered_at && (
                <span className="ml-2">• Scanned {new Date(selectedSnapshot.discovered_at).toLocaleString()}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[500px] mt-4">
            {schemaLoading ? (
              <div className="space-y-3">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
            ) : selectedSnapshot?.databases?.length > 0 ? (
              <div className="space-y-3">
                {selectedSnapshot.databases.map((dbInfo) => (
                  <div key={dbInfo.name} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button onClick={() => toggleDb(dbInfo.name)}
                      className="w-full flex items-center justify-between p-3 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/10 transition-colors">
                      <div className="flex items-center gap-3">
                        {expandedDbs[dbInfo.name] ? <ChevronDown className="w-4 h-4 text-[#D4AF37]" /> : <ChevronRight className="w-4 h-4 text-[#D4AF37]" />}
                        <Database className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-gray-900 font-semibold">{dbInfo.name}</span>
                        {dbInfo.error && <AlertCircle className="w-3.5 h-3.5 text-red-400" />}
                      </div>
                      <span className="text-xs text-gray-500">{dbInfo.tables?.length || 0} tables</span>
                    </button>

                    {expandedDbs[dbInfo.name] && (
                      <div className="p-2 space-y-1">
                        {dbInfo.error ? (
                          <p className="text-red-500 text-sm p-2">Error: {dbInfo.error}</p>
                        ) : dbInfo.tables?.length > 0 ? (
                          dbInfo.tables.map((table) => {
                            const tableKey = `${dbInfo.name}.${table.name}`;
                            return (
                              <div key={tableKey} className="border border-gray-100 rounded-lg overflow-hidden">
                                <button onClick={() => toggleTable(tableKey)}
                                  className="w-full flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                                  <div className="flex items-center gap-3">
                                    {expandedTables[tableKey] ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                                    <Table className="w-3.5 h-3.5 text-[#D4AF37]" />
                                    <span className="text-gray-900 font-medium text-sm">{table.name}</span>
                                  </div>
                                  <span className="text-xs text-gray-500" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                    {(table.row_count || 0).toLocaleString()} rows
                                  </span>
                                </button>
                                {expandedTables[tableKey] && (
                                  <div className="p-3 border-t border-gray-100 bg-white space-y-3">
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="text-gray-500 text-xs uppercase">
                                          <th className="text-left py-1.5 px-2">Column</th>
                                          <th className="text-left py-1.5 px-2">Type</th>
                                          <th className="text-left py-1.5 px-2">Null</th>
                                          <th className="text-left py-1.5 px-2">Key</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(table.columns || []).map((col, idx) => (
                                          <tr key={idx} className="border-t border-gray-100">
                                            <td className="py-1.5 px-2 text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{col.Field}</td>
                                            <td className="py-1.5 px-2 text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{col.Type}</td>
                                            <td className="py-1.5 px-2 text-gray-500">{col.Null}</td>
                                            <td className="py-1.5 px-2">
                                              {col.Key && (
                                                <span className={`text-xs px-1.5 py-0.5 rounded ${col.Key === 'PRI' ? 'bg-amber-100 text-amber-700' : col.Key === 'MUL' ? 'bg-blue-100 text-blue-700' : col.Key === 'UNI' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                                                  {col.Key}
                                                </span>
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {table.sample_row && (
                                      <div className="mt-2">
                                        <p className="text-xs font-medium text-gray-500 mb-1">Sample Row:</p>
                                        <pre className="text-xs bg-gray-50 p-2 rounded border border-gray-100 overflow-x-auto" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                          {JSON.stringify(table.sample_row, null, 2)}
                                        </pre>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-gray-500 text-sm p-2">No tables found</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No schema data found. Run discovery first.</div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConnectionsPage;
